import FF from "@/components/forms/FormField.module.css";
import TF from "@/components/forms/TextField.module.css";

import { useAriaField } from "@/components/forms/ariaFieldRegister.ts";
import { CreateFormAction } from "@/components/forms/CreateFormAction.tsx";
import { FormField } from "@/components/forms/FormField.tsx";
import { IconRadio } from "@/components/forms/IconRadio.tsx";
import { MonthlyCostField } from "@/components/forms/MonthlyCostField.tsx";
import { RadioGroupDiv } from "@/components/forms/RadioGroupDiv.tsx";
import { WHookForm } from "@/components/forms/WHookForm.tsx";
import { WRadioGroup } from "@/components/forms/WRadioGroup.tsx";
import { WTextField, type WTextFieldProps } from "@/components/forms/WTextField.tsx";
import { HeroBlock } from "@/components/HeroBlock.tsx";
import { Separator } from "@/components/Separator.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { BillingAccountField } from "@/modules/billing/forms/BillingAccountField.tsx";
import { getSizeParams } from "@/modules/compute/commonViewModel.ts";
import { getDefaultDesignatedPoolForLocation, getPoolsForLocation } from "@/modules/compute/pool/pool.ts";
import type { VirtualMachineLoc } from "@/modules/compute/vmQuery.ts";
import { VmSizeSelect } from "@/modules/compute/VmSizeSelect.tsx";
import { getDiskPackages, getDiskRanges, SIZE_DISABLED, type SizeValue } from "@/modules/compute/vmSizeSelectUtils.ts";
import { AttachedToSelect } from "@/modules/disks/AttachedToSelect.tsx";
import type { DiskResourceId } from "@/modules/disks/disk/resourceId.ts";
import { attachDiskMutation, createDiskMutation } from "@/modules/disks/disksListQuery.ts";
import { diskLink } from "@/modules/disks/links.ts";
import { LocationField } from "@/modules/location/LocationField.tsx";
import { useLocationsForType } from "@/modules/location/locationQuery.ts";
import { resourceIdToString, UNASSIGNED_RESOURCE } from "@/modules/network/ipAddress/resourceId.ts";
import { canCreateResourceHostPoolsAtom } from "@/modules/pools/hostPoolsQuery.ts";
import { pricesAtom } from "@/modules/pricing/pricingQuery.ts";
import { getDiskTotalPriceBySize } from "@/modules/pricing/resourcePricing.ts";
import { cn } from "@/utils/classNames.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { Navigate } from "@tanstack/react-router";
import type { DiskCreateBody } from "@warrenio/api-spec/spec.oats.gen";
import { useAtomValue } from "jotai/react";
import { useState } from "react";
import { Input } from "react-aria-components";
import { Controller, type SubmitHandler, useForm, useFormContext } from "react-hook-form";
import { last } from "remeda";

function diskSizeToSizeValue(diskSize: number): SizeValue {
    return {
        disks: diskSize,
        vcpu: SIZE_DISABLED,
        ram: SIZE_DISABLED,
        isCustom: true,
    };
}

export interface DiskCreateInputs extends Omit<DiskCreateBody, "storage_pool_uuid"> {
    host_pool_uuid: string | null;
    location: string;
}

export function DiskCreate({ inputs }: { inputs?: Partial<DiskCreateInputs> }) {
    //#region Hooks
    const { t } = useLingui();
    const createMutation = useStandardMutation(createDiskMutation);
    const attachMutation = useStandardMutation(attachDiskMutation);
    const locations = useLocationsForType("disk");
    const { data: pools } = useAtomValue(canCreateResourceHostPoolsAtom);

    const [attachedTo, setAttachedTo] = useState<DiskResourceId | null>(null);
    //#endregion Hooks

    const defaultPool = getDefaultDesignatedPoolForLocation(pools, locations.defaultLocation);
    const form = useForm<DiskCreateInputs>({
        disabled: createMutation.isPending,
        defaultValues: {
            location: locations.defaultLocation,
            host_pool_uuid: defaultPool?.uuid,
            size_gb: 20, // Or a sensible static default
            ...inputs,
        },
    });

    const { watch, control, setValue } = form;

    const selectedLocation = watch("location");
    const selectedPoolUuid = watch("host_pool_uuid");

    const locationPools = getPoolsForLocation(pools, selectedLocation).filter((l) => l.is_visible);

    const currentPool = locationPools.find((p) => p.uuid === selectedPoolUuid);

    const defaultOS = { os_name: "", os_version: "" };
    const sizeParams = getSizeParams(currentPool?.guest_limits);
    const diskRanges = getDiskRanges(sizeParams, undefined);
    const packages = getDiskPackages(defaultOS, last(diskRanges)!);

    if (createMutation.isSuccess) {
        // We can navigate to the VM view (even though the invalidated list query has not necessarily finished yet)
        // because of the optimistic update in the mutation's `onSuccess` handler.
        return <Navigate {...diskLink(createMutation.data)} />;
    }

    const onSubmit: SubmitHandler<DiskCreateInputs> = async ({ location, ...body }) => {
        const { uuid } = await createMutation.mutateAsync({
            location,
            body: { ...body, storage_pool_uuid: getStoragePoolUuid(body.host_pool_uuid) },
        });
        if (attachedTo) {
            await attachMutation.mutateAsync({
                body: { uuid: attachedTo.$id, storage_uuid: uuid },
                location,
            });
        }
    };

    function getStoragePoolUuid(value: string | null) {
        if (!value) return undefined;

        const pool = locationPools.find((p) => p.uuid === value);
        return pool?.storage_pool_uuid;
    }

    function attachTo(item: VirtualMachineLoc | UNASSIGNED_RESOURCE) {
        setAttachedTo(
            item
                ? {
                      $type: "virtual_machine",
                      $id: item.uuid,
                      location: item.location,
                  }
                : UNASSIGNED_RESOURCE,
        );
    }

    return (
        <>
            <CreateHero />

            <Separator />

            <WHookForm form={form} onSubmit={onSubmit}>
                {locations.showLocations && (
                    <>
                        <LocationField
                            locations={locations}
                            afterChange={(location) => {
                                const defaultPool = getDefaultDesignatedPoolForLocation(pools, location);
                                setValue("host_pool_uuid", defaultPool?.uuid ?? null);
                            }}
                        />
                        <Separator />
                    </>
                )}

                {locationPools.length ? (
                    <Controller
                        control={control}
                        name="host_pool_uuid"
                        render={({ field }) => (
                            <WRadioGroup
                                wide
                                label={t`Server class`}
                                isRequired
                                value={field.value}
                                onChange={field.onChange}
                            >
                                {locationPools.map((item) => (
                                    <IconRadio
                                        wide
                                        icon="jp-cluster-icon"
                                        id={item.uuid}
                                        title={item.name}
                                        key={item.uuid}
                                        desc={item.description}
                                        {...item}
                                    />
                                ))}
                            </WRadioGroup>
                        )}
                    />
                ) : null}

                <Controller
                    control={control}
                    name="size_gb"
                    render={({ field }) => (
                        <FormField wide label={t`Size`} isRequired>
                            <RadioGroupDiv>
                                <VmSizeSelect
                                    vmData={{
                                        location: watch("location"),
                                        designated_pool_uuid: watch("host_pool_uuid") ?? undefined,
                                        ...defaultOS,
                                    }}
                                    sliderRanges={{
                                        ssd: diskRanges,
                                        cpu: [],
                                        ram: [],
                                    }}
                                    value={diskSizeToSizeValue(field.value ?? diskRanges[0])}
                                    onChange={(sizeValue) => field.onChange(sizeValue.disks)}
                                    packages={packages}
                                    diskOnly
                                    isDiskPrimary
                                    sizeParams={sizeParams}
                                />
                            </RadioGroupDiv>
                        </FormField>
                    )}
                />

                <Separator />

                <FormField
                    label={t`Attached to`}
                    description={t`Choose a virtual machine to attach the disk to, or leave it detached and connect it to a virtual machine later.`}
                >
                    <AttachedToSelect
                        storagePoolUuid={getStoragePoolUuid(watch("host_pool_uuid"))}
                        valueKey={resourceIdToString(attachedTo)}
                        location={watch("location")}
                        onChange={(value) => attachTo(value)}
                    />
                </FormField>

                <DisplayNameField />

                <Separator />

                <DiskMonthlyCostField
                    size={diskSizeToSizeValue(watch("size_gb") ?? diskRanges[0])}
                    location={watch("location")}
                    hostPoolUuid={watch("host_pool_uuid")}
                    storagePoolUuid={getStoragePoolUuid(watch("host_pool_uuid"))}
                    billingAccountId={watch("billing_account_id")}
                />

                <BillingAccountField resourceType="disk" isRequired />

                <Separator />

                <CreateFormAction resourceType="disk" />
            </WHookForm>
        </>
    );
}

function CreateHero() {
    const { t } = useLingui();
    return (
        <HeroBlock title={t`Create new`} icon="jp-ssd-disk-icon">
            <Trans>
                Create additional storage for your workloads. Configure the disk and attach it to a virtual machine now
                or later.
            </Trans>
        </HeroBlock>
    );
}

function DisplayNameField(textFieldProps: WTextFieldProps) {
    const { t } = useLingui();
    const form = useFormContext<DiskCreateInputs>();
    const { ref, props } = useAriaField("display_name", form, {
        isRequired: textFieldProps.isRequired,
    });
    return (
        <WTextField {...props} label={t`Resource name`} {...textFieldProps}>
            <Input ref={ref} className={cn(FF.FormFieldInput, TF.Input)} />
        </WTextField>
    );
}

function DiskMonthlyCostField({
    size,
    location,
    hostPoolUuid,
    storagePoolUuid,
    billingAccountId,
}: {
    size: SizeValue;
    location: string;
    hostPoolUuid: string | null;
    storagePoolUuid: string | undefined;
    billingAccountId?: number;
}) {
    //#region Hooks
    const priceList = useSuspenseQueryAtom(pricesAtom);
    //#endregion
    const price = getDiskTotalPriceBySize(priceList, size.disks, location, hostPoolUuid, storagePoolUuid);

    return (
        <MonthlyCostField
            // TODO: Need to somehow consider storagePoolUuid in price calculation
            price={price}
            defaultLocation={location}
            defaultHostPool={hostPoolUuid}
            billingAccountId={billingAccountId}
        />
    );
}
